import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Paper,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import InfiniteScroll from "react-infinite-scroll-component";
import { backgroundColor, SFColors, windDirectionChange } from "src/store/common";
import { alertMessages, humidityColors, precip, solarRadiationColors, temperatureColors, vpdColors, windGustColors } from "src/constants";
import { useStoreContext } from "src/store/context";
import { useAddAlert } from "src/store/actions";
import moment from "moment-timezone";
import { getWeatherObsStations } from "src/apis/getWeatherCalls";
import arrow from 'src/assets/images/arrow.png';


type WxHourlyProps = {
    station: any,
    startDate: any,
    endDate: any,
}


export const WxHourly: React.FC<WxHourlyProps> = ({ station, startDate,endDate}) =>{
    const [ , dispatch] = useStoreContext();
    const [wxHourlyData, setWxHourlyData] = useState<any[]>([]);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const addAlert = useAddAlert(dispatch);
    const [wxHourlyFailCount, setWxHourlyFailCount] = useState<number>(0);
    /**
     * Callback that will be called upon data initialization
     */
  const wxHourlyCallback = useCallback((count:number) => {
    (async () => {
      try {
        const zeroedEndDate = moment(endDate).utc().hour(23).minute(59).second(59).millisecond(59);
        const zeroedStartDate = moment(startDate).utc().hour(0).minute(0).second(0).millisecond(0)

        const request = {
          startDate: zeroedStartDate.utc().format(),
          endDate: zeroedEndDate.utc().format(),
          stationId: station.station_id?.toString(),
          zoomLevel: 5,
          page: 0,
          perPage: 25,
          sortBy: 'observation_time',
          sortOrder: 'desc'
        }

        const response: any = await getWeatherObsStations(request, addAlert);
        if(response?.data){
          setWxHourlyFailCount(0)
          setWxHourlyData(response.data)
          if((response._metadata.page ===0 && response._metadata.page_count ===1)||(response._metadata.page=== response._metadata.page_count)){
            setHasMore(false)
          }
          else{
            setHasMore(true)
          }
        }
      } catch (error) {
        console.log('error', error);
        setHasMore(false);
        setWxHourlyFailCount(count+1)
        addAlert({
          id: '',
          message: alertMessages.retrievingStationData,
          type: 'error-info',
          icon: 'fa-exclamation-triangle'
        });
      }
    })();
  },[startDate, endDate, station, addAlert, setWxHourlyFailCount]);

  const fetchMoreData = useCallback(() =>{
    (async () => {
      try {
        const zeroedEndDate = moment(endDate).utc().hour(23).minute(59).second(59).millisecond(59);
        const zeroedStartDate = moment(startDate).utc().hour(0).minute(0).second(0).millisecond(0)        
        
        const request = {
          startDate: zeroedStartDate,
          endDate: zeroedEndDate,
          stationId: station.station_id?.toString(),
          zoomLevel: 5,
          page: page,
          perPage: 25,
          sortBy: 'observation_time',
          sortOrder: 'desc'
        }

        const response: any = await getWeatherObsStations(request, addAlert);
        
        setWxHourlyData((prevData) => [...prevData, ...response.data]);
        response.data.length > 0 ? setHasMore(true) : setHasMore(false);
        setPage(page + 1);
      } catch (error) {
        console.log('error', error);
        addAlert({
          id: '',
          message: alertMessages.retrievingStationData,
          type: 'error-info',
          icon: 'fa-exclamation-triangle'
        });
      }
    })();
  },[startDate, endDate, station, page, addAlert]);

  /**
   * Initialize the dataset when modifying the parameters
   */
  useEffect(() => {
    if(station){
      setPage(1);
      setHasMore(true);
      wxHourlyCallback(wxHourlyFailCount);
    }
    if(station && wxHourlyFailCount>0 && wxHourlyFailCount<4){
      wxHourlyCallback(wxHourlyFailCount);
    }
  },[startDate, endDate, station, wxHourlyCallback, wxHourlyFailCount]);
  
  return (
    <Box>
        <Paper sx ={{width: '99%'}}>
          <TableContainer className="tableWx smooth-scrollbar" id="scrollableTable">
              <TableHead className="tableHead"                                
                style={{
                  position: "sticky",
                  top: 0,
                  display: 'flex',
                  zIndex: 110,
                  backgroundColor: "#A1A1A1",
                }}>
              <TableRow >
                <TableCell>Date: HR (LST)</TableCell>
                <TableCell>Temp(°F)</TableCell>
                <TableCell>RH(%)</TableCell>
                <TableCell>Precip(in)</TableCell>
                <TableCell>WS(mph)</TableCell>
                <TableCell>WD(°)</TableCell>
                <TableCell>GS(mph)</TableCell>
                <TableCell>SR(W/m2)</TableCell>
                <TableCell>VPD(Pa)</TableCell>
                <TableCell>SF</TableCell>
              </TableRow>
          </TableHead>          
          
          {station?.weather_hourly_visible === false &&
            <TableBody>
              <div style={{ padding: '15px' }}>
                Due to restrictions in place by the network owner, <span style={{ fontWeight: '700' }}>no hourly</span> data is available for this <span style={{ fontWeight: '700' }}>restricted network</span>.
              </div>
            </TableBody>
          }
              {!hasMore && wxHourlyData?.length===0? (
                station?.weather_hourly_visible &&
                <TableBody>
                  <div style={{padding: '15px'}}>
                    No Data Available for this Station for the Selected Date Range
                  </div>
                </TableBody>
              ):(
                station?.weather_hourly_visible &&
                <InfiniteScroll
                  dataLength={wxHourlyData?.length}
                  next={fetchMoreData}
                  hasMore={hasMore}
                  loader={<p>Loading...</p>}
                  scrollableTarget="scrollableTable"
                >
              <TableBody>
                {station?.weather_hourly_visible && wxHourlyData?.map((row, index) => (
                  <TableRow
                    key={`${row.observation_time_lst} ${index}`}
                    style={{
                      width: '100%',
                      border: row.observation_type === "F" ? "3px dotted #222222" : "2px solid #222222",
                    }}
                  >
                    <TableCell
                      style={{
                        backgroundColor: "rgb(175 158 158 / 87%)",
                        position: "sticky",
                        left: 0,
                        zIndex: 100,
                      }}
                    >
                      {`${moment(row.display_hour_lst).utcOffset(station.time_zone_offset *60).format('MM-DD-YYYY HH:mm')} (${station.time_zone})`}
                    </TableCell>
                    <TableCell
                      style={{
                        backgroundColor: backgroundColor(row.temperature, temperatureColors).bg,
                        color: backgroundColor(row.temperature, temperatureColors).text,
                        border: row.observation_type === "F" ? "3px dotted #222222" : "2px solid #222222",
                      }}
                    >
                      {row.temperature}
                    </TableCell>
                    <TableCell
                      style={{
                        backgroundColor: backgroundColor(row.relative_humidity, humidityColors).bg,
                        color: backgroundColor(row.relative_humidity, humidityColors).text,
                        border: row.observation_type === "F" ? "3px dotted #222222" : "2px solid #222222",
                        borderColor: 'black',

                      }}
                    >
                      {row.relative_humidity}
                    </TableCell>
                    <TableCell
                      style={{
                        backgroundColor: backgroundColor(row.hourly_precip, precip).bg,
                        color: backgroundColor(row.hourly_precip, precip).text,
                        border: row.observation_type === "F" ? "3px dotted #222222" : "2px solid #222222",
                        borderColor: '#222222',
                      }}
                    >
                      {row.hourly_precip}
                    </TableCell>
                    <TableCell
                      style={{
                        backgroundColor: backgroundColor(row.wind_speed, windGustColors).bg,
                        color: backgroundColor(row.wind_speed, windGustColors).text,
                        border: row.observation_type === "F" ? "3px dotted #222222" : "2px solid #222222",
                        borderColor: '#222222',

                      }}
                    >
                      {row.wind_speed}
                    </TableCell>
                    <TableCell
                      style={{
                        backgroundColor: 'white',
                        color: 'black',
                        border: row.observation_type === "F" ? "3px dotted #222222" : "2px solid #222222",
                        borderColor: '#222222',

                      }}
                    >
                      {`${row.wind_direction}   `}
                      {<img src={arrow} style={{ width: '30px', transform: 'rotate(' + windDirectionChange(row.wind_direction - 90) + 'deg)' }} alt={`${row.wind_direction}`} />}
                    </TableCell>
                    <TableCell
                      style={{
                        backgroundColor: backgroundColor(row.peak_gust_speed, windGustColors).bg,
                        color: backgroundColor(row.peak_gust_speed, windGustColors).text,
                        border: row.observation_type === "F" ? "3px dotted #222222" : "2px solid #222222",
                        borderColor: '#222222',

                      }}
                    >
                      {row.peak_gust_speed}
                    </TableCell>
                    <TableCell
                      style={{
                        backgroundColor: backgroundColor(row.sol_rad, solarRadiationColors).bg,
                        color: backgroundColor(row.sol_rad, solarRadiationColors).text,
                        border: row.observation_type === "F" ? "3px dotted #222222" : "2px solid #222222",
                        borderColor: '#222222',
  
                    }}
                    >
                      {row.sol_rad}
                    </TableCell>
                    <TableCell
                      style={{
                        backgroundColor: backgroundColor(row.vapor_press_def, vpdColors).bg,
                        color: backgroundColor(row.vapor_press_def, vpdColors).text,
                        border: row.observation_type === "F" ? "3px dotted #222222" : "2px solid #222222",
                        borderColor: '#222222',
  
                    }}
                    >
                      {row.vapor_press_def}
                    </TableCell>
                    <TableCell
                      style={{
                        backgroundColor: SFColors(row.snow_flag).bg,
                        color: SFColors(row.snow_flag).text,
                        border: row.observation_type === "F" ? "3px dotted #222222" : "2px solid #222222",
                        borderColor: '#222222',
  
                    }}
                    >
                      {(row.snow_flag)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </InfiniteScroll>
              )}
          </TableContainer>
        </Paper>    
    </Box>
  );
}
